import { RefObject, useLayoutEffect } from 'react';

import styles from './responsiveTable.module.scss';

const HEADER_TRANSLATE_VAR = '--table-header-translate';
const HEADER_HEIGHT_VAR = '--table-header-height';
const TABLE_HEIGHT_VAR = '--table-height';
const HEADER_TOP_VAR = '--table-sticky-top';
const TABLE_BOTTOM_INSET_VAR = '--table-bottom-inset';

// A view timeline uses its subject's nearest scrolling ancestor. Only enable
// the native viewport path when no intervening ancestor captures that timeline.
const canUseScrollTimeline = (wrapper: HTMLElement): boolean => {
  if (
    typeof CSS === 'undefined' ||
    !CSS.supports?.('animation-timeline', '--table-header') ||
    !CSS.supports('animation-range', 'exit-crossing -1px exit-crossing calc(100% - 2px)')
  ) {
    return false;
  }
  let ancestor = wrapper.parentElement;
  while (
    ancestor &&
    ancestor !== document.body &&
    ancestor !== document.documentElement
  ) {
    if (/^(auto|scroll|hidden|overlay)$/.test(getComputedStyle(ancestor).overflowY)) {
      return false;
    }
    ancestor = ancestor.parentElement;
  }
  return true;
};

const getStickyHeaderOffset = (
  tableTop: number,
  tableHeight: number,
  headerHeight: number,
  topOffset: number,
): number =>
  Math.min(Math.max(0, topOffset - tableTop), Math.max(0, tableHeight - headerHeight));

// overflow-x scrolling prevents native sticky cells from tracking the viewport.
// Move the original header inside the same table, keeping horizontal scrolling,
// column widths, sort/selection controls and accessible associations intact.
const useStickyTableHeader = (
  wrapperRef: RefObject<HTMLDivElement>,
  tableRef: RefObject<HTMLTableElement>,
  enabled: boolean,
  topOffset: string,
  inContainer = false,
): void => {
  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const table = tableRef.current;
    const header = table?.tHead;
    if (!enabled || !wrapper || !table || !header) return undefined;
    if (inContainer) {
      const updateEdge = (): void => {
        wrapper.classList.toggle(styles.headerScrolled, wrapper.scrollTop > 0);
      };
      updateEdge();
      wrapper.addEventListener('scroll', updateEdge, { passive: true });
      return () => {
        wrapper.removeEventListener('scroll', updateEdge);
        wrapper.classList.remove(styles.headerScrolled);
      };
    }
    let frame: number | null = null;
    let previousOffset: number | undefined;
    let resolvedTop = 0;
    let headerHeight = 0;
    const nativeScroll = canUseScrollTimeline(wrapper);

    const update = (): void => {
      const bounds = table.getBoundingClientRect();
      const offset = getStickyHeaderOffset(
        bounds.top,
        bounds.height,
        headerHeight,
        resolvedTop,
      );
      if (offset !== previousOffset) {
        wrapper.style.setProperty(HEADER_TRANSLATE_VAR, `${offset}px`);
        wrapper.classList.toggle(styles.headerScrolled, offset > 0);
        previousOffset = offset;
      }
    };
    const measure = (): void => {
      frame = null;
      // Resolve CSS lengths only on layout changes, not on every scroll.
      resolvedTop = parseFloat(getComputedStyle(wrapper).scrollMarginTop) || 0;
      headerHeight = header.offsetHeight;
      if (nativeScroll) {
        const tableHeight = table.getBoundingClientRect().height;
        const wrapperHeight = wrapper.getBoundingClientRect().height;
        wrapper.style.setProperty(HEADER_TOP_VAR, `${resolvedTop}px`);
        wrapper.style.setProperty(
          HEADER_HEIGHT_VAR,
          `${header.getBoundingClientRect().height}px`,
        );
        wrapper.style.setProperty(TABLE_HEIGHT_VAR, `${tableHeight}px`);
        // A horizontal scrollbar belongs to the timeline's wrapper, not to
        // the table. Exclude its height so the header stops at the table bottom.
        wrapper.style.setProperty(
          TABLE_BOTTOM_INSET_VAR,
          `${Math.max(0, wrapperHeight - tableHeight)}px`,
        );
      } else {
        update();
      }
    };
    const schedule = (): void => {
      if (frame !== null) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    measure();
    wrapper.classList.toggle(styles.nativeScroll, nativeScroll);
    // Supported browsers synchronize the transform with scrolling themselves.
    // Keep the event-based path only as a compatibility fallback.
    if (!nativeScroll) {
      window.addEventListener('scroll', update, { capture: true, passive: true });
    }
    window.addEventListener('resize', schedule);
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : undefined;
    observer?.observe(wrapper);
    observer?.observe(table);
    observer?.observe(header);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
      wrapper.classList.remove(styles.nativeScroll);
      wrapper.classList.remove(styles.headerScrolled);
      [
        HEADER_TRANSLATE_VAR,
        HEADER_TOP_VAR,
        HEADER_HEIGHT_VAR,
        TABLE_HEIGHT_VAR,
        TABLE_BOTTOM_INSET_VAR,
      ].forEach(property => wrapper.style.removeProperty(property));
    };
  }, [wrapperRef, tableRef, enabled, topOffset, inContainer]);
};

export { getStickyHeaderOffset, useStickyTableHeader };
